import React, { useEffect, useMemo, useState } from "react";
import { enhanceFilterGroup, stripFilterMeta } from "../utils/filterUtils";
import { cloneGroup, createEmptyCondition } from "./filter/filterBuilderConstants";
import FilterGroup from "./filter/FilterGroup";

const AdvancedFilterBuilder = ({ value, onChange, fields }) => {
  const fieldMap = useMemo(
    () => fields.reduce((acc, field) => { acc[field.id] = field; return acc; }, {}),
    [fields]
  );

  const [internalGroup, setInternalGroup] = useState(() => enhanceFilterGroup(value));

  useEffect(() => { setInternalGroup(enhanceFilterGroup(value)); }, [value]);

  const broadcastChange = (nextGroup) => {
    setInternalGroup(nextGroup);
    onChange(stripFilterMeta(nextGroup, fieldMap));
  };

  const addConditionToGroup = (groupId) => {
    const cloned = cloneGroup(internalGroup);
    const attach = (group) => {
      if (group.id === groupId) { group.conditions.push(createEmptyCondition()); return true; }
      return group.conditions.some((c) => Array.isArray(c.conditions) && attach(c));
    };
    attach(cloned);
    broadcastChange(cloned);
  };

  const addGroupToGroup = (groupId) => {
    const cloned = cloneGroup(internalGroup);
    const newGroup = {
      id: `group_${Date.now()}_${Math.random().toString(16).slice(2)}`,
      logic: "and",
      conditions: [createEmptyCondition()],
    };
    const attach = (group) => {
      if (group.id === groupId) { group.conditions.push(newGroup); return true; }
      return group.conditions.some((c) => Array.isArray(c.conditions) && attach(c));
    };
    attach(cloned);
    broadcastChange(cloned);
  };

  const removeConditionById = (conditionId) => {
    const cloned = cloneGroup(internalGroup);
    const prune = (group) => {
      group.conditions = group.conditions.filter((c) => {
        if (Array.isArray(c.conditions)) { prune(c); return c.conditions.length > 0; }
        return c.id !== conditionId;
      });
    };
    prune(cloned);
    broadcastChange(cloned);
  };

  const updateGroupLogic = (groupId, logic) => {
    const cloned = cloneGroup(internalGroup);
    const apply = (group) => {
      if (group.id === groupId) { group.logic = logic; return true; }
      return group.conditions.some((c) => Array.isArray(c.conditions) && apply(c));
    };
    apply(cloned);
    broadcastChange(cloned);
  };

  const removeGroupById = (groupId) => {
    if (internalGroup.id === groupId) { broadcastChange(enhanceFilterGroup()); return; }
    const cloned = cloneGroup(internalGroup);
    const prune = (group) => {
      group.conditions = group.conditions.filter((c) => {
        if (Array.isArray(c.conditions)) {
          if (c.id === groupId) return false;
          prune(c);
          return c.conditions.length > 0;
        }
        return true;
      });
    };
    prune(cloned);
    broadcastChange(cloned);
  };

  const updateConditionField = (conditionId, fieldId) => {
    const cloned = cloneGroup(internalGroup);
    const apply = (group) => {
      group.conditions.forEach((c) => {
        if (Array.isArray(c.conditions)) { apply(c); }
        else if (c.id === conditionId) { c.field = fieldId; c.operator = ""; c.value = ""; c.values = []; }
      });
    };
    apply(cloned);
    broadcastChange(cloned);
  };

  const updateConditionOperator = (conditionId, operator) => {
    const cloned = cloneGroup(internalGroup);
    const apply = (group) => {
      group.conditions.forEach((c) => {
        if (Array.isArray(c.conditions)) { apply(c); }
        else if (c.id === conditionId) {
          c.operator = operator;
          if (operator === "between") { c.values = ["", ""]; c.value = ""; }
          else if (operator === "in" || operator === "notin") { c.values = []; c.value = ""; }
          else { c.value = ""; c.values = []; }
        }
      });
    };
    apply(cloned);
    broadcastChange(cloned);
  };

  const updateConditionValue = (conditionId, valueIndex, value, isArrayValue) => {
    const cloned = cloneGroup(internalGroup);
    const apply = (group) => {
      group.conditions.forEach((c) => {
        if (Array.isArray(c.conditions)) { apply(c); }
        else if (c.id === conditionId) {
          if (isArrayValue) {
            const next = Array.isArray(c.values) ? [...c.values] : [];
            next[valueIndex] = value;
            c.values = next;
          } else {
            c.value = value;
          }
        }
      });
    };
    apply(cloned);
    broadcastChange(cloned);
  };

  return (
    <div className="space-y-4">
      <FilterGroup
        group={internalGroup}
        depth={0}
        allowRemove={false}
        fields={fields}
        fieldMap={fieldMap}
        addConditionToGroup={addConditionToGroup}
        addGroupToGroup={addGroupToGroup}
        removeConditionById={removeConditionById}
        updateGroupLogic={updateGroupLogic}
        removeGroupById={removeGroupById}
        updateConditionField={updateConditionField}
        updateConditionOperator={updateConditionOperator}
        updateConditionValue={updateConditionValue}
      />
    </div>
  );
};

export default AdvancedFilterBuilder;
